import { useMemo, type ReactNode } from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
	ExclamationTriangleIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Button, Spinner } from '../ui';
import ServiceCard from './ServiceCard';
import { filterServices } from './serviceFilter';
import type { Service } from '../../stores/servicesStore';
import type { CategoryFilter } from '../../types/serviceCategory';

export interface ServiceListViewProps {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	searchTerm: string;
	onSearchChange: (term: string) => void;
	/**
	 * Whether the viewer may create, edit, or delete. When false the Add button
	 * and the per-card Edit/Delete actions are not rendered at all — a
	 * view-only staff member sees the cards and nothing else.
	 */
	canManage: boolean;
	onView: (service: Service) => void;
	onAdd?: () => void;
	onEdit?: (service: Service) => void;
	onDelete?: (service: Service) => void;
	onRetry?: () => void;

	/**
	 * Provider-only extras. Everything below is optional and inert when omitted,
	 * which is how the staff Services page keeps its original behaviour.
	 */
	categoryFilter?: CategoryFilter;
	/** Passing this turns on checkboxes and drag-to-assign. */
	onToggleSelect?: (id: string, extendRange: boolean) => void;
	selectedIds?: ReadonlySet<string>;
	draggingIds?: ReadonlySet<string>;
	/** Rendered next to the search box — the "select all matching" control. */
	toolbarSlot?: ReactNode;
}

interface DraggableServiceCardProps {
	service: Service;
	canManage: boolean;
	onView: (service: Service) => void;
	onEdit?: (service: Service) => void;
	onDelete?: (service: Service) => void;
	onToggleSelect: (id: string, extendRange: boolean) => void;
	isSelected: boolean;
	selectionActive: boolean;
	isDragging: boolean;
}

/**
 * A card that can be picked up and dropped on a category chip. Split out
 * because `useDraggable` is a hook: the plain card must not call it, both so
 * the staff list stays free of drag behaviour and so it can render outside a
 * `DndContext`.
 */
const DraggableServiceCard = ({ service, ...rest }: DraggableServiceCardProps) => {
	const { setNodeRef, listeners, attributes } = useDraggable({
		id: service.id,
		data: { serviceId: service.id },
		// Drag is a mouse accelerator, not a route to the feature — the checkbox
		// and the bulk bar are the accessible path. Keeping the body out of the
		// tab order avoids a focusable element that does nothing when activated.
		attributes: {
			role: 'group',
			roleDescription: 'Service card — drag onto a category to file it',
			tabIndex: -1,
		},
	});

	return (
		<ServiceCard
			service={service}
			dragRef={setNodeRef}
			dragListeners={listeners}
			dragAttributes={attributes}
			{...rest}
		/>
	);
};

/**
 * The search + card grid that both the Provider Settings → Services tab and the
 * Staff Services page render. Filtering lives here so the two can't drift.
 */
const ServiceListView = ({
	services,
	isLoading,
	error,
	searchTerm,
	onSearchChange,
	canManage,
	onView,
	onAdd,
	onEdit,
	onDelete,
	onRetry,
	categoryFilter = 'all',
	onToggleSelect,
	selectedIds,
	draggingIds,
	toolbarSlot,
}: ServiceListViewProps) => {
	const filteredServices = useMemo(
		() => filterServices(services, searchTerm, categoryFilter),
		[services, searchTerm, categoryFilter]
	);

	const selectionActive = !!selectedIds && selectedIds.size > 0;
	const isFiltered = !!searchTerm.trim() || categoryFilter !== 'all';

	return (
		<>
			{/* Mobile Add Button — the desktop one lives in the panel header */}
			{canManage && onAdd && (
				<Button variant="primary" onClick={onAdd} className="w-full mb-4 sm:hidden">
					<PlusIcon className="w-4 h-4 mr-1" />
					Add New Service
				</Button>
			)}

			{/* Search Bar */}
			{!isLoading && !error && services.length > 0 && (
				<div className="service-toolbar">
					<div className="service-search">
						<MagnifyingGlassIcon className="service-search__icon" />
						<input
							type="text"
							className="service-search__input"
							placeholder="Search services..."
							value={searchTerm}
							onChange={e => onSearchChange(e.target.value)}
						/>
					</div>
					{toolbarSlot}
				</div>
			)}

			{isLoading ? (
				<div className="py-12 text-center">
					<Spinner
						size="lg"
						className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
					/>
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading services...</p>
				</div>
			) : error ? (
				<div className="py-12 text-center">
					<ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-300 dark:text-red-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Failed to load services
					</h3>
					<p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
					{onRetry && (
						<Button variant="secondary" onClick={onRetry}>
							Try Again
						</Button>
					)}
				</div>
			) : filteredServices.length === 0 ? (
				<div className="py-12 text-center">
					<WrenchScrewdriverIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						{isFiltered ? 'No services found' : 'No Services Yet'}
					</h3>
					<p className="text-gray-500 dark:text-gray-400 mb-4">
						{searchTerm.trim()
							? 'Try a different search term'
							: categoryFilter === 'uncategorized'
								? 'Every service is filed under a category.'
								: categoryFilter !== 'all'
									? 'Nothing is filed under this category yet. Select services and assign them here.'
									: canManage
										? 'Get started by adding your first service.'
										: 'No services have been set up for this location yet.'}
					</p>
					{!isFiltered && canManage && onAdd && (
						<Button variant="primary" onClick={onAdd}>
							<PlusIcon className="w-4 h-4 mr-1" />
							Add First Service
						</Button>
					)}
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{filteredServices.map(service =>
						onToggleSelect ? (
							<DraggableServiceCard
								key={service.id}
								service={service}
								canManage={canManage}
								onView={onView}
								onEdit={onEdit}
								onDelete={onDelete}
								onToggleSelect={onToggleSelect}
								isSelected={!!selectedIds?.has(service.id)}
								selectionActive={selectionActive}
								isDragging={!!draggingIds?.has(service.id)}
							/>
						) : (
							<ServiceCard
								key={service.id}
								service={service}
								canManage={canManage}
								onView={onView}
								onEdit={onEdit}
								onDelete={onDelete}
							/>
						)
					)}
				</div>
			)}
		</>
	);
};

export default ServiceListView;
